import { Elements } from '@stripe/react-stripe-js'
import { loadStripe, StripeElementsOptionsMode } from '@stripe/stripe-js'
import { NextPage } from 'next'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { Box } from '~/components/layout'
import { useModal } from '~/components/modal'
import { KindestPaymentForm } from '~/components/payment-form'
import { ChariotPaymentForm } from '~/components/payment-form-chariot'
import { Loading } from '~/components/payment-form/components/loading'
import { Heading, Meta } from '~/components/typography'
import { env } from '~/config/env'
import { routes } from '~/config/routes'
import { PageBySlugQuery } from '~/graphql/page-by-slug'
import { useUtmQuery } from '~/hooks/use-utm-query'
import { FundraisingPageLayout } from '~/layouts/fundraising-page-layout'
import { colors } from '~/styles/colors'
import { DonationRecurrenceInterval } from '~/types/graphql'
import { trackConversion } from '~/utils/integrations'

import { BackButton } from './components/back-button'
import { SelectDonation } from './components/select-donation'
import Stepper from './components/stepper'
import {
  ChariotConfirmBanner,
  HeaderColorStripe,
  HeaderContent,
  MetaWrapper,
  StepWrapper,
} from './style'

const ModalConfirm = dynamic(() => import('~/components/modal-confirm'))

// import { CoverImage } from '~/components/cover-image'
type Props = {
  page: PageBySlugQuery['pageBySlug']
  fundraiser?: PageBySlugQuery['pageBySlug']['fundraisers'][0]
}

const stripe = loadStripe(env.stripeKey)

export const FundraisingPageCheckout: NextPage<Props> = ({
  page,
  fundraiser,
}) => {
  const [activeStepIndex, setActiveStepIndex] = useState<number>(0)

  const router = useRouter()
  const { query } = useRouter()
  const utmQuery = useUtmQuery()
  const isUsingChariot = Boolean(query.chariot)
  const modalConfirm = useModal()

  useEffect(() => {
    return () => {
      sessionStorage.clear()
    }
  }, [])

  const {
    title,
    // imageUrl, TODO: ??
    nonprofit,
    slug,
    googleAdsConversionId,
    googleAdsConversionLabel,
  } = page

  const [stripeOptions, setStripeOptions] =
    useState<StripeElementsOptionsMode>()

  useEffect(() => {
    if (sessionStorage && sessionStorage?.formData && activeStepIndex == 1) {
      const storage = JSON.parse(sessionStorage.formData)

      setStripeOptions({
        mode: 'payment',
        currency: 'usd',
        amount: storage.amount,
        setup_future_usage:
          storage.paymentFrequency == DonationRecurrenceInterval.None
            ? undefined
            : 'off_session',
        // INFO: if needed in the future, apply set of payment methods to
        // set which methods should be available
        // Be careful not to forget about BE - this change needs to applied
        // also for payment intent creation
        // INFO^2: idealy get a list of options from BE
        // payment_method_types: ['card', 'us_bank_account'],
        // payment_method_types: [],
        appearance: {
          theme: 'stripe',
        },
      })
    }
  }, [activeStepIndex])

  const navigateToDetail = () => {
    router.replace(routes.fundraisingPage.home(slug!, fundraiser?.id).as)
  }

  const handleSuccess = (donationAmount: number, orderId: string) => {
    trackConversion(
      'Donation Page',
      donationAmount,
      page.title,
      googleAdsConversionId,
      googleAdsConversionLabel
    )
    console.log('Handle success', orderId)
    router.replace(
      `${
        routes.fundraisingPage.home(slug!, fundraiser?.id).as
      }?donation=${orderId}`
    )
  }

  const steps = [
    // TODO: parse steps into separate pages?
    <SelectDonation
      key={0}
      onSubmit={() => {
        window.scrollTo(0, 0)
        setActiveStepIndex(1)
      }}
      page={page}
    />,
    <>
      {stripeOptions?.amount ? (
        <Elements key={1} stripe={stripe} options={stripeOptions}>
          <KindestPaymentForm
            page={page}
            onSuccess={handleSuccess}
            fundraiserId={fundraiser?.id}
          />
        </Elements>
      ) : (
        <Loading />
      )}
    </>,
    <ChariotPaymentForm
      key={2}
      page={page}
      onSuccess={handleSuccess}
      fundraiserId={fundraiser?.id}
    />,
  ]

  const getActiveStep = () => {
    return isUsingChariot ? steps[2] : steps[activeStepIndex]
  }

  const pageLabel = page.type === "event"  ? 'Event Page' : 'Fundraising Page';

  const backButtons = [
    <BackButton
      key={0}
      onClick={() => {
        isUsingChariot ? modalConfirm.show() : navigateToDetail()
      }}
      query={utmQuery}
      // shallow
    >
      {`Back to ${pageLabel}`}
    </BackButton>,
    <BackButton
      key={1}
      onClick={() => {
        setActiveStepIndex(0)
      }}
    >
      Back to Choose Your Donation
    </BackButton>,
  ]

  const getBackButtonLabel = (isUsingChariot?: boolean) => {
    return isUsingChariot ? backButtons[0] : backButtons[activeStepIndex]
  }

  const titles = ['Choose Your Donation', 'Payment Details']

  const getTabTitle = (isUsingChariot?: boolean) => {
    return isUsingChariot ? 'Donor Information' : titles[activeStepIndex]
  }

  return (
    <FundraisingPageLayout
      page={page}
      leftContent={getBackButtonLabel(isUsingChariot)}
    >
      <>
        <HeaderColorStripe>
          <HeaderContent>
            <Heading level={3} bold marginBottom={6}>
              {getTabTitle(isUsingChariot)}
            </Heading>
            <Meta regular>{title}</Meta>
            {isUsingChariot ? (
              <ChariotConfirmBanner>
                <Meta>
                  Your donor-advised fund account has been successfully linked!
                </Meta>
              </ChariotConfirmBanner>
            ) : (
              <Box marginTop={12}>
                <Stepper stepsAmount={2} activeStepIndex={activeStepIndex} />
              </Box>
            )}
          </HeaderContent>
        </HeaderColorStripe>
        <StepWrapper>{getActiveStep()}</StepWrapper>
        <MetaWrapper regular color={colors.slateGray} padding="1rem 0">
          {nonprofit.taxNote}
        </MetaWrapper>
      </>
      <ModalConfirm
        heading={`Back to ${pageLabel}?`}
        message={`Are you sure you want to go back to the ${pageLabel}?`}
        note="Your progress will be lost and any linked funds within the Chariot Donor Advised Fund form will be unlinked."
        submitLabel="Go Back"
        cancelLabel="Cancel"
        onSubmit={() => navigateToDetail()}
        danger
        {...modalConfirm}
      />
    </FundraisingPageLayout>
  )
}
